import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../../../api/api";
import "./Breadcrumbs.css";

export default function Breadcrumbs() {
  const location = useLocation();
  const pathname = location.pathname;
  const { getPageHierarchy } = useApi();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();

  // Parse the pathname to extract route segments
  // Structure: /:regionId/:planId/:stockId
  // Root path (/) is the home page - no region selected
  const pathParts = pathname.split("/").filter(Boolean); // ['new-england', 'ne-multi-groundfish', 'redfish']
  
  // Only extract IDs if we're not on the home page
  const isHomePage = pathname === "/";
  const regionSlug = isHomePage ? null : pathParts[0];
  const planSlug = pathParts[1];
  const stockSlug = pathParts[2];

  // Dropdown state
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false);
  const [stockDropdownOpen, setStockDropdownOpen] = useState(false);
  const [navigationData, setNavigationData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs for dropdown containers
  const regionDropdownRef = useRef(null);
  const planDropdownRef = useRef(null);
  const stockDropdownRef = useRef(null);

  // Fetch page hierarchy from API (region -> plan -> stock tree)
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setNavigationData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchHierarchy = async () => {
      try {
        setLoading(true);
        const response = await getPageHierarchy('stock');
        if (cancelled) return;
        // Hierarchy root is home node; its children are regions (sort by page_order when present)
        const rawRegions = response?.data?.[0]?.children ?? [];
        const byPageOrder = (a, b) => (a.page_order ?? 999) - (b.page_order ?? 999);
        const regions = [...rawRegions].sort(byPageOrder);
        const transformedData = regions.map((region) => {
          const rawPlans = region.children ?? [];
          const plans = [...rawPlans].sort(byPageOrder).map((plan) => {
            const rawStocks = plan.children ?? [];
            const stocks = [...rawStocks].sort(byPageOrder).map((stock) => ({
              slug: stock.slug,
              name: stock.name,
            }));
            return { slug: plan.slug, name: plan.name, stocks };
          });
          return { slug: region.slug, name: region.name, plans };
        });
        setNavigationData(transformedData);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching page hierarchy:', error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHierarchy();
    return () => {
      cancelled = true;
    };
  }, [getPageHierarchy, authLoading, isAuthenticated]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setRegionDropdownOpen(false);
      }
      if (planDropdownRef.current && !planDropdownRef.current.contains(event.target)) {
        setPlanDropdownOpen(false);
      }
      if (stockDropdownRef.current && !stockDropdownRef.current.contains(event.target)) {
        setStockDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get current values from navigation data
  const availableRegions = navigationData || [];
  const isInitialNavigationLoad = loading && !navigationData;
  const currentRegion = regionSlug ? navigationData?.find(r => r.slug === regionSlug) : null;
  const currentRegionName = currentRegion?.name || (isInitialNavigationLoad ? "Loading Regions..." : "Select a Region");
  const availablePlans = currentRegion?.plans || [];
  const currentPlan = availablePlans.find(p => p.slug === planSlug);
  const currentPlanName = currentPlan?.name || (isInitialNavigationLoad ? "Loading Plans..." : null);
  const availableStocks = currentPlan?.stocks || [];
  const currentStockName = availableStocks.find(s => s.slug === stockSlug)?.name || (isInitialNavigationLoad ? "Loading Stocks..." : null);

  return (
    <nav className="breadcrumbs">
      <span className="breadcrumb-logo">
        <Link to="/">KNUDSEN.EARTH - FisheriesOS - US</Link>
      </span>
      
      {/* Region Dropdown */}
      <>
        <span className="breadcrumb-separator"> / </span>
        <div className="breadcrumb-dropdown" ref={regionDropdownRef}>
          <button
            className="breadcrumb-dropdown-button"
            disabled={isInitialNavigationLoad}
            onClick={() => {
              setRegionDropdownOpen(!regionDropdownOpen);
              setPlanDropdownOpen(false);
              setStockDropdownOpen(false);
            }}
          >
            {currentRegionName}
            <span className="breadcrumb-dropdown-arrow">▼</span>
          </button>
          {regionDropdownOpen && (
            <div className="breadcrumb-dropdown-menu">
              {availableRegions.length > 0 ? (
                [...availableRegions].sort((a, b) => a.name.localeCompare(b.name)).map((region) => (
                  <Link
                    key={region.slug}
                    to={`/${region.slug}`}
                    className={`breadcrumb-dropdown-item ${regionSlug && region.slug === regionSlug ? 'active' : ''}`}
                    onClick={() => setRegionDropdownOpen(false)}
                  >
                    {region.name}
                  </Link>
                ))
              ) : loading ? (
                <div className="breadcrumb-dropdown-item disabled">
                  Loading regions...
                </div>
              ) : (
                <div className="breadcrumb-dropdown-item disabled">
                  No regions available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Plan Dropdown - Show only when we're on a region page or deeper (regionId exists) */}
        {regionSlug && (
          <>
            <span className="breadcrumb-separator"> / </span>
            <div className="breadcrumb-dropdown" ref={planDropdownRef}>
              <button
                className="breadcrumb-dropdown-button"
                disabled={isInitialNavigationLoad}
                onClick={() => {
                  setPlanDropdownOpen(!planDropdownOpen);
                  setRegionDropdownOpen(false);
                  setStockDropdownOpen(false);
                }}
              >
                {currentPlanName || "Select a Plan"}
                <span className="breadcrumb-dropdown-arrow">▼</span>
              </button>
              {planDropdownOpen && (
                <div className="breadcrumb-dropdown-menu">
                  {availablePlans.length > 0 ? (
                    [...availablePlans].sort((a, b) => a.name.localeCompare(b.name)).map((plan) => (
                      <Link
                        key={plan.slug}
                        to={`/${regionSlug}/${plan.slug}`}
                        className={`breadcrumb-dropdown-item ${plan.slug === planSlug ? 'active' : ''}`}
                        onClick={() => setPlanDropdownOpen(false)}
                      >
                        {plan.name}
                      </Link>
                    ))
                  ) : loading ? (
                    <div className="breadcrumb-dropdown-item disabled">
                      Loading plans...
                    </div>
                  ) : (
                    <div className="breadcrumb-dropdown-item disabled">
                      No plans available
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Stock Dropdown - Show only when we're on a plan page or deeper (planId exists) */}
        {planSlug && (
          <>
            <span className="breadcrumb-separator"> / </span>
            <div className="breadcrumb-dropdown" ref={stockDropdownRef}>
              <button
                className="breadcrumb-dropdown-button"
                disabled={isInitialNavigationLoad}
                onClick={() => {
                  setStockDropdownOpen(!stockDropdownOpen);
                  setRegionDropdownOpen(false);
                  setPlanDropdownOpen(false);
                }}
              >
                {currentStockName || "Select a Stock"}
                <span className="breadcrumb-dropdown-arrow">▼</span>
              </button>
              {stockDropdownOpen && (
                <div className="breadcrumb-dropdown-menu">
                  {availableStocks.length > 0 ? (
                    [...availableStocks].sort((a, b) => a.name.localeCompare(b.name)).map((stock) => (
                      <Link
                        key={stock.slug}
                        to={`/${regionSlug}/${planSlug}/${stock.slug}`}
                        className={`breadcrumb-dropdown-item ${stock.slug === stockSlug ? 'active' : ''}`}
                        onClick={() => setStockDropdownOpen(false)}
                      >
                        {stock.name}
                      </Link>
                    ))
                  ) : loading ? (
                    <div className="breadcrumb-dropdown-item disabled">
                      Loading stocks...
                    </div>
                  ) : (
                    <div className="breadcrumb-dropdown-item disabled">
                      No stocks available
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </>
    </nav>
  );
}
